import React, { useCallback, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { InlineEditInput } from 'src/components/generic/InlineEditInput';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { useCreateTask } from 'src/hooks/tasks';
import { makeStyles } from 'tss-react/mui';

import { useAddPhase } from './use-add-phase';

const useStyles = makeStyles()(theme => ({
  addTaskInput: {
    fontSize: 14,
    padding: theme.spacing(0.5, 1),
  },
  addTaskInputRoot: {
    width: '100%',
  },
  addTaskRow: {
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(0, 1),
  },
}));

export interface AddTaskComposerOptions {
  onCreated: (task: ApolloTypes.TaskSimpleFragment) => void;
  /** The workflow's phase. Null when it has none yet; the first task creates it. */
  phaseId: UUID | null;
  workflowId: UUID;
  assignees?: ApolloTypes.TaskAssigneeInput[];
  /** Called when the field is left empty or Escape is pressed. */
  onDismiss?: () => void;
  /** Create the task already started. */
  start?: boolean;
}

interface AddTaskInputProps extends AddTaskComposerOptions {
  autoFocus?: boolean;
}

/** The "Add a task..." row: type a title and press Enter or leave the field. */
export function AddTaskInput({ autoFocus, ...options }: AddTaskInputProps) {
  const { classes } = useStyles();
  const composer = useAddTaskComposer(options);

  return (
    <div className={classes.addTaskRow}>
      <InlineEditInput
        autoFocus={autoFocus}
        classes={{ input: classes.addTaskInput, root: classes.addTaskInputRoot }}
        placeholder="Add a task..."
        value={composer.title}
        onBlur={composer.onBlur}
        onChange={composer.onChange}
        onKeyDown={composer.onKeyDown}
      />
    </div>
  );
}

/**
 * State and handlers for a field that creates a task from its title: Enter or leaving the field
 * creates it, and an empty field or Escape dismisses.
 */
export function useAddTaskComposer({
  assignees,
  onCreated,
  onDismiss,
  phaseId,
  start,
  workflowId,
}: AddTaskComposerOptions) {
  const [title, setTitle] = useState('');
  const { createTask } = useCreateTask();
  const { addPhase } = useAddPhase(workflowId);
  // A blur right after Enter must not create the same task a second time.
  const isSubmittingRef = useRef(false);

  const submit = useCallback(async () => {
    if (isSubmittingRef.current) return;
    const trimmed = title.trim();
    if (!trimmed) {
      onDismiss?.();
      return;
    }
    isSubmittingRef.current = true;
    try {
      const targetPhaseId = phaseId ?? (await addPhase({ isSigning: false }));
      if (!targetPhaseId) return;
      const result = await createTask(workflowId, {
        variables: {
          input: {
            assignees,
            props: {
              description: '',
              phaseId: targetPhaseId,
              startedAt: start ? new Date().toISOString() : null,
              title: trimmed,
              workflowId,
            },
          },
        },
      });
      setTitle('');
      if (result.data?.createTask.task) onCreated(result.data.createTask.task);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [addPhase, assignees, createTask, onCreated, onDismiss, phaseId, start, title, workflowId]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') void submit();
      if (e.key === 'Escape') {
        setTitle('');
        onDismiss?.();
      }
    },
    [onDismiss, submit]
  );
  const onBlur = useCallback(() => void submit(), [submit]);
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTitle(e.target.value),
    []
  );

  return { onBlur, onChange, onKeyDown, title };
}
